import {
	ChangeDetectionStrategy,
	Component,
	computed,
	input,
	DestroyRef,
	OnInit,
	ViewContainerRef
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoService } from '@jsverse/transloco';

import { OrganizationsStoreService } from '@shure/cloud/shared/services/organizations-store-service';
import { generateColorFromName } from '@shure/cloud/shared/ui/components';
import { ApiUsersService } from '@shure/cloud/shared/users/data-access';
import { PrismSnackbarService } from '@shure/prism-angular-components';

import { SwitchOrganizationService } from './services/switch-organization.service';

@Component({
	selector: 'sh-switch-organization-main',
	templateUrl: './switch-organization-main.component.html',
	styleUrls: ['./switch-organization-main.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class SwitchOrganizationMainComponent implements OnInit {
	public readonly contentOrientation = input<'rowLayout' | 'columnLayout'>('columnLayout');

	public accountInformationSignal = this.organizationsStore.accountInformation;

	public initials = computed(() => {
		const orgName = this.accountInformationSignal()?.organizationDetails?.organizationName ?? '';
		if (!orgName) return { firstInitial: '', secondInitial: '' };

		const orgNameParts = orgName.trim().split(' ');
		return {
			firstInitial: orgNameParts[0]?.charAt(0).toUpperCase() || '',
			secondInitial: orgNameParts[1]?.charAt(0).toUpperCase() || ' '
		};
	});

	public colorIndex = computed(() => {
		return generateColorFromName(this.organizationName());
	});

	public organizationName = computed(() => {
		const orgName = this.accountInformationSignal()?.organizationDetails?.organizationName ?? '';
		return orgName;
	});

	constructor(
		private switchOrganizationService: SwitchOrganizationService,
		private organizationsStore: OrganizationsStoreService,
		private vcr: ViewContainerRef,
		private apiUsersService: ApiUsersService,
		private readonly destroyRef: DestroyRef,
		private snackBarService: PrismSnackbarService,
		private translocoService: TranslocoService
	) {}

	public ngOnInit(): void {
		const orgDetails = this.accountInformationSignal()?.organizationDetails;
		if (!orgDetails) {
			this.getAccountInfo();
		}
	}

	public openChangeOrganization(): void {
		this.switchOrganizationService.openChangeOrgDialog(this.vcr);
	}

	/**
	 * Fetches session account information from the API.
	 * The response is processed, and if valid data is found, it updates the organization store with the fetched account info.
	 */
	public getAccountInfo(): void {
		// const params =
		// 	this.projections().length > 0
		// 		? {
		// 				projections: this.projections().join(',')
		// 		  }
		// 		: undefined;
		this.apiUsersService
			.getSessionAccountInfo$Response()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (accountInfoResp) => {
					if (accountInfoResp?.body?.body) {
						this.organizationsStore.setAccountInfo(accountInfoResp.body.body);
					}
				},
				error: (error) => {
					const errorMessage = error.error;
					if (errorMessage?.i18nKey) {
						this.snackBarService.open({
							message: this.translocoService.translate(
								'cloud.shared.error-labels.' + errorMessage.i18nKey
							),
							actionText: this.translocoService.translate('common.snackbar.ok')
						});
					} else {
						this.snackBarService.open({
							message: this.translocoService.translate('cloud.shared.error-labels.fail-to-fetch-data'),
							actionText: this.translocoService.translate('common.snackbar.ok')
						});
					}
				}
			});
	}
}
